import api, { handleResponse, handleError } from './api';

/**
 * Door check-in — host/co-host ticket scanning + live dashboard for one event,
 * plus the list of the viewer's currently-live events for the persistent banner.
 */
const doorService = {
  /** Scan a ticket code (application code or individual ticket code) at an event's door. */
  async scan(eventId, code) {
    try {
      const response = await api.post(`/events/${eventId}/door/scan`, { code });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Check a party in by name (no QR). Same DoorScanResultDto as a scan:
   * result 'ok' | 'already' | 'invalid'. Host/co-host only.
   */
  async checkIn(eventId, applicationId) {
    try {
      const response = await api.post(`/events/${eventId}/door/checkin`, { applicationId });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Live counts + recent scans for the door dashboard. */
  async summary(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/door/summary`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Events the viewer hosts/co-hosts that are live right now (drives the door banner). */
  /** A page of the door list: scope is 'in' | 'left' | 'all'. */
  async roster(eventId, { scope = 'all', q = '', skip = 0, take = 25 } = {}) {
    try {
      const params = new URLSearchParams({ scope, skip: String(skip), take: String(take) });
      if (q) params.set('q', q);
      return handleResponse(await api.get(`/events/${eventId}/door/roster?${params}`));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Events I hold a ticket for right now — the guest's side of the door window. */
  async activeTickets() {
    try {
      const tzOffset = new Date().getTimezoneOffset();
      return handleResponse(await api.get(`/tickets/active?tzOffset=${tzOffset}`));
    } catch (error) {
      return handleError(error);
    }
  },

  async active() {
    try {
      // The banner opens at midnight on the event's own day, so the server needs
      // the viewer's UTC offset to know when that is.
      const tzOffset = new Date().getTimezoneOffset();
      const response = await api.get(`/door/active?tzOffset=${tzOffset}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
};

export default doorService;
